import CompanyArticleList from '@src/pages/company/article/CompanyArticleList';
import CompanyArticleDetail from '@src/pages/company/article/CompanyArticleShow';
import BizsolCallback from '@src/pages/company/auth/bizsol/BizsolCallback';
import BizsolIntroduction from '@src/pages/company/auth/bizsol/CompanyBizsolIntroduction';
import CompanyLogin from '@src/pages/company/auth/CompanyLogin';
import CompanyLogout from '@src/pages/company/auth/CompanyLogout';
import CompanyPasswordEdit from '@src/pages/company/auth/CompanyPasswordEdit';
import CompanyResetPassword from '@src/pages/company/auth/CompanyResetPassword';
import CompanySendResetPasswordEmail from '@src/pages/company/auth/CompanySendResetPasswordEmail';
import CompanyTwoFactor from '@src/pages/company/auth/CompanyTwoFactor';
import CompanyEdit from '@src/pages/company/company/CompanyEdit';
import CompanyRegistration from '@src/pages/company/company-user/CompanyRegistration';
import CompanyRegistrationComplete from '@src/pages/company/company-user/CompanyRegistrationComplete';
import CompanyRegistrationCreate from '@src/pages/company/company-user/CompanyRegistrationCreate';
import CompanyRegistrationRequiresApproval from '@src/pages/company/company-user/CompanyRegistrationRequiresApproval';
import CompanyUserCreate from '@src/pages/company/company-user/CompanyUserCreate';
import CompanyUserEdit from '@src/pages/company/company-user/CompanyUserEdit';
import CompanyUserList from '@src/pages/company/company-user/CompanyUserList';
import CompanyHome from '@src/pages/company/CompanyHome';
import CompanyDxDiagnosisAnswers from '@src/pages/company/dx-diagnosis/CompanyDxDiagnosisAnswers';
import CompanyDxDiagnosisExplanation from '@src/pages/company/dx-diagnosis/CompanyDxDiagnosisExplanation';
import CompanyDxDiagnosisIntroduction from '@src/pages/company/dx-diagnosis/CompanyDxDiagnosisIntroduction';
import CompanyDxDiagnosisList from '@src/pages/company/dx-diagnosis/CompanyDxDiagnosisList';
import CompanyDxDiagnosisQuestion from '@src/pages/company/dx-diagnosis/CompanyDxDiagnosisQuestion';
import CompanyDxDiagnosisResults from '@src/pages/company/dx-diagnosis/CompanyDxDiagnosisResults';
import CompanyEventList from '@src/pages/company/events/CompanyEventList';
import CompanyEventsShow from '@src/pages/company/events/CompanyEventsShow';
import Profile from '@src/pages/company/profile/Profile';
import FooterPrivacyPolicy from '@src/pages/company/static/PrivacyPolicy';
import FooterTerms from '@src/pages/company/static/Terms';
import CompanySupportServicesDetail from '@src/pages/company/support-service/CompanySupportServicesDetail';
import CompanySupportServicesList from '@src/pages/company/support-service/CompanySupportServicesList';
import { responsiveStylesType } from '@src/types/responsiveStylesType';

type CompanyRouteType = {
  path: string;
  title: string;
  description?: string;
  element: React.ReactElement;
  responsive: responsiveStylesType;
};

export const COMPANY_URL = {
  HOME: '/',
  LOGIN: '/login',
  TWO_FACTOR: '/two-factor',
  PROFILE: '/profile',
  REGISTRATION: '/registration',
  REGISTRATION_CREATE: '/registration/create',
  REGISTRATION_COMPLETE: '/registration/complete',
  REGISTRATION_REQUIRES_APPROVAL: '/registration/requires-approval',
  PASSWORD_EDIT: '/password/edit',
  PASSWORD_RESET_SENDMAIL: '/password/reset',
  PASSWORD_RESET: '/password/reset/:token',
  ARTICLES: '/articles',
  ARTICLES_SHOW: '/articles/:id',
  EVENTS_SHOW: '/events/:id',
  EVENTS: '/events',
  SUPPORT_SERVICES: '/support-services',
  SUPPORT_SERVICES_SHOW: '/support-services/:id',
  DX_DIAGNOSIS: '/dx-diagnosis',
  DX_DIAGNOSIS_QUESTION: '/dx-diagnosis/:id/questions/:stepId',
  DX_DIAGNOSIS_ANSWERS: '/dx-diagnosis/:id/answers',
  DX_DIAGNOSIS_RESULTS: '/dx-diagnosis/:id/results',
  DX_DIAGNOSIS_INTRODUCTION: '/dx-diagnosis/introduction',
  DX_DIAGNOSIS_EXPLANATION: '/dx-diagnosis/explanation',
  COMPANY_USERS_CREATE: '/company-users/create',
  COMPANY_USERS_EDIT: '/company-users/:id/edit',
  COMPANY_USERS: '/company-users',
  COMPANIES_EDIT: '/companies/edit',
  TERMS: '/terms',
  PRIVACY_POLICY: '/privacy-policy',
  BIZSOL_CALLBACK: '/bizsol/callback',
  BIZSOL_INTRODUCTION: '/bizsol/introduction',
  LOGOUT: '/logout',
  CONTACT:
    import.meta.env.VITE_COMPANY_CONTACT_URL || `${import.meta.env.VITE_APP_BASE_URL}/contact`,
};

export interface ShowProps {
  setFeature: (feature: string) => void;
  setDescription?: (description: string) => void;
}

const companyRoutes: CompanyRouteType[] = [
  // トップページ
  {
    path: COMPANY_URL.HOME,
    title: '',
    description: import.meta.env.VITE_ARTICLES_META_DESCRIPTION,
    element: <CompanyHome />,
    responsive: false,
  },
  // ログイン
  {
    path: COMPANY_URL.LOGIN,
    title: 'ログイン',
    description: import.meta.env.VITE_GENERAL_META_DESCRIPTION,
    element: <CompanyLogin />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // ログイン（BisSOLログイン）
  {
    path: COMPANY_URL.BIZSOL_INTRODUCTION,
    title: '遷移先選択',
    element: <BizsolIntroduction />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 認証コード入力
  {
    path: COMPANY_URL.TWO_FACTOR,
    title: '認証コード入力',
    element: <CompanyTwoFactor />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 初回パスワード変更
  {
    path: COMPANY_URL.PASSWORD_EDIT,
    title: 'パスワード設定',
    element: <CompanyPasswordEdit />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // パスワード再発行（メール送信）
  {
    path: COMPANY_URL.PASSWORD_RESET_SENDMAIL,
    title: 'パスワード設定',
    element: <CompanySendResetPasswordEmail />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // パスワード再発行（再設定）
  {
    path: COMPANY_URL.PASSWORD_RESET,
    title: 'パスワード設定',
    element: <CompanyResetPassword />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // プロフィール
  {
    path: COMPANY_URL.PROFILE,
    title: 'プロフィール',
    element: <Profile />,
    responsive: ['desktop', 'mobile'],
  },
  // 企業アカウント登録1
  {
    path: COMPANY_URL.REGISTRATION,
    title: '新規会員登録',
    description: import.meta.env.VITE_GENERAL_META_DESCRIPTION,
    element: <CompanyRegistration />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 企業アカウント登録2
  {
    path: COMPANY_URL.REGISTRATION_CREATE,
    title: '新規会員登録',
    element: <CompanyRegistrationCreate />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 企業アカウント登録完了
  {
    path: COMPANY_URL.REGISTRATION_COMPLETE,
    title: '新規会員登録',
    element: <CompanyRegistrationComplete />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 企業アカウント登録承認待ち
  {
    path: COMPANY_URL.REGISTRATION_REQUIRES_APPROVAL,
    title: '新規会員登録',
    element: <CompanyRegistrationRequiresApproval />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 記事一覧
  {
    path: COMPANY_URL.ARTICLES,
    title: '',
    description: import.meta.env.VITE_ARTICLES_META_DESCRIPTION,
    element: <CompanyArticleList />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 記事閲覧
  {
    path: COMPANY_URL.ARTICLES_SHOW,
    // CMSの値を動的に出力
    title: '',
    element: <CompanyArticleDetail />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // イベント閲覧
  {
    path: COMPANY_URL.EVENTS_SHOW,
    // CMSの値を動的に出力
    title: '',
    element: <CompanyEventsShow />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // イベント一覧
  {
    path: COMPANY_URL.EVENTS,
    title: 'イベント一覧',
    description: import.meta.env.VITE_EVENTS_META_DESCRIPTION,
    element: <CompanyEventList />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 支援サービス一覧
  {
    path: COMPANY_URL.SUPPORT_SERVICES,
    title: '支援サービス一覧',
    description: import.meta.env.VITE_SUPPORT_SERVICES_META_DESCRIPTION,
    element: <CompanySupportServicesList />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 支援サービス閲覧
  {
    path: COMPANY_URL.SUPPORT_SERVICES_SHOW,
    // CMSの値を動的に出力
    title: '',
    element: <CompanySupportServicesDetail />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // DX診断一覧
  {
    path: COMPANY_URL.DX_DIAGNOSIS,
    title: 'DX診断一覧',
    element: <CompanyDxDiagnosisList />,
    responsive: ['desktop', 'tablet'],
  },
  // DX診断導入
  {
    path: COMPANY_URL.DX_DIAGNOSIS_EXPLANATION,
    title: '設問前説明',
    element: <CompanyDxDiagnosisExplanation />,
    responsive: ['desktop', 'tablet'],
  },
  // DX診断設問
  {
    path: COMPANY_URL.DX_DIAGNOSIS_QUESTION,
    title: 'DX診断設問回答',
    element: <CompanyDxDiagnosisQuestion />,
    responsive: ['desktop', 'tablet'],
  },
  // DX診断回答
  {
    path: COMPANY_URL.DX_DIAGNOSIS_ANSWERS,
    title: 'DX診断回答確認',
    element: <CompanyDxDiagnosisAnswers />,
    responsive: ['desktop', 'tablet'],
  },
  // DX診断結果
  {
    path: COMPANY_URL.DX_DIAGNOSIS_RESULTS,
    title: 'DX診断結果',
    element: <CompanyDxDiagnosisResults />,
    responsive: ['desktop', 'tablet'],
  },
  // DX診断紹介
  {
    path: COMPANY_URL.DX_DIAGNOSIS_INTRODUCTION,
    title: 'DX診断紹介',
    description: import.meta.env.VITE_DX_DIAGNOSIS_INTRODUCTION_META_DESCRIPTION,
    element: <CompanyDxDiagnosisIntroduction />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // ユーザー一覧
  {
    path: COMPANY_URL.COMPANY_USERS,
    title: 'ユーザー管理',
    description: import.meta.env.VITE_GENERAL_META_DESCRIPTION,
    element: <CompanyUserList />,
    responsive: ['desktop'],
  },
  // ユーザー登録
  {
    path: COMPANY_URL.COMPANY_USERS_CREATE,
    title: 'ユーザー管理',
    description: import.meta.env.VITE_GENERAL_META_DESCRIPTION,
    element: <CompanyUserCreate />,
    responsive: ['desktop'],
  },
  // ユーザー編集
  {
    path: COMPANY_URL.COMPANY_USERS_EDIT,
    title: 'ユーザー管理',
    description: import.meta.env.VITE_GENERAL_META_DESCRIPTION,
    element: <CompanyUserEdit />,
    responsive: ['desktop'],
  },
  // 企業情報編集
  {
    path: COMPANY_URL.COMPANIES_EDIT,
    description: import.meta.env.VITE_GENERAL_META_DESCRIPTION,
    title: '企業編集',
    element: <CompanyEdit />,
    responsive: ['desktop'],
  },
  // 利用規約
  {
    path: COMPANY_URL.TERMS,
    title: '利用規約',
    element: <FooterTerms />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // プライバシーポリシー
  {
    path: COMPANY_URL.PRIVACY_POLICY,
    title: 'プライバシーポリシー',
    element: <FooterPrivacyPolicy />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // ログアウト
  {
    path: COMPANY_URL.LOGOUT,
    title: 'ログアウト',
    element: <CompanyLogout />,
    responsive: false,
  },
  // BizSOLコールバックURL
  {
    path: COMPANY_URL.BIZSOL_CALLBACK,
    title: '',
    element: <BizsolCallback />,
    responsive: false,
  },
];

export default companyRoutes;
